// components/CVSessionsSection.jsx
import React from 'react';
import styled from 'styled-components';

const Section = styled.section`
  padding: 120px 2rem;
  background: #000000;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100vw;
    height: 1px;
    background: #333333;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100vw;
  }
`;

const Container = styled.div`
  max-width: 1350px;
  margin: 0 auto;
  width: 100%;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const Square = styled.div`
  width: 15px;
  height: 15px;
  background: #ffffff;
  flex-shrink: 0;
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  color: #ffffff;
  margin: 0;
  font-weight: 500;
  white-space: nowrap;
  letter-spacing: 0.5px;
`;

/* 4 cards on desktop */
const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PricingCard = styled.div`
  background: #0b0b0b;
  border: 1px solid #2a2a2a;
  padding: 3rem 2rem;
  min-height: 420px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  position: relative;

  /* Fade-up animation */
  opacity: 0;
  transform: translateY(30px);
  animation: fadeUp 0.7s ease-out forwards;
  animation-delay: ${props => props.delay || '0s'};

  /* Optional "Most Popular" ribbon */
  ${props => props.popular && `
    border: 1px solid #ffffff33;
    &::before {
      content: 'MOST POPULAR';
      position: absolute;
      top: -12px;
      left: 50%;
      transform: translateX(-50%);
      background: #ffffff;
      color: #000000;
      font-size: 0.75rem;
      font-weight: 700;
      padding: 4px 16px;
      letter-spacing: 1px;
    }
  `}

  h3 {
    font-size: 1.5rem;
    color: #ffffff;
    margin: 0 0 1rem 0;
    font-weight: 700;
  }

  .price {
    font-size: 3rem;
    font-weight: bold;
    color: #ffffff;
    margin: 0.5rem 0 1.8rem;
  }

  ul {
    flex: 1;
    margin: 0 0 2rem 0;
    padding-left: 0;
    list-style: none;
    color: #cbd5e1;
    font-size: 1rem;
    line-height: 1.8;
  }

  li {
    margin-bottom: 0.75rem;
  }

  li:last-child {
    margin-bottom: 0;
  }

  button {
    margin-top: auto;
    background: transparent;
    border: 1px solid #ffffff;
    border-radius: 0px;
    color: #ffffff;
    padding: 1rem 1.5rem;
    font-size: 1rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      background: #ffffff;
      color: #000000;
    }
  }

  @keyframes fadeUp {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 768px) {
    padding: 2.5rem 2rem;
    min-height: auto;
  }
`;

const CVSessionsSection = () => {
  const packages = [
    {
      name: "CV Review",
      price: "£60",
      features: [
        "Professional feedback on your existing CV and amendments to content and layout that will set you apart."
      ],
      // This is the exact URL from your original Next.js code
      bookingUrl: "https://book.squareup.com/appointments/66p0ka1whe03e2/location/LE63V5JQBS5C7/services/VHAWXGZB6HOV6WB5NYE5CPEI"
    },
    {
      name: "CV Session Plus",
      price: "£180",
      features: [
        "One to one advice that is specific to your search, specialist insight, market trend information and comprehensive review of your CV, to ensure it best reflects you and your experience.",
      ],
      bookingUrl: "https://book.squareup.com/appointments/66p0ka1whe03e2/location/LE63V5JQBS5C7/services/VHAWXGZB6HOV6WB5NYE5CPEI"
    },
    {
      name: "CV Session Premium",
      price: "£250",
      popular: true,
      features: [
        "Bespoke advice and assistance tailored to your job hunt. This level offers all the benefits of Plus with the added bonus of ongoing support and preparation"
      ],
      bookingUrl: "https://book.squareup.com/appointments/66p0ka1whe03e2/location/LE63V5JQBS5C7/services/VHAWXGZB6HOV6WB5NYE5CPEI"
    },
    {
      name: "CV Session Advanced",
      price: "£750",
      features: [
        "Professional guidance and honest advice for your career journey, whether it is career advancement or complete career shift. This confidential service encourages professional development along with the Premium benefits."
      ],
      bookingUrl: "https://book.squareup.com/appointments/66p0ka1whe03e2/location/LE63V5JQBS5C7/services/VHAWXGZB6HOV6WB5NYE5CPEI"
    }
  ];

  return (
    <Section id="cv-sessions">
      <Container>
        <TitleRow>
          <Square />
          <SectionTitle>CV Sessions</SectionTitle>
        </TitleRow>

        <PricingGrid>
          {packages.map((pkg, index) => (
            <PricingCard 
              key={index} 
              delay={`${index * 0.12}s`}
              popular={pkg.popular}
            >
              <h3>{pkg.name}</h3>
              <div className="price">{pkg.price}</div>
              <ul>
                {pkg.features.map((feature, i) => (
                  <li key={i}>{feature}</li>
                ))}
              </ul>
              <button
                onClick={() => window.open(pkg.bookingUrl, '_blank', 'noopener,noreferrer')}
              >
                Book Now
              </button>
            </PricingCard>
          ))}
        </PricingGrid>
      </Container>
    </Section>
  );
};

export default CVSessionsSection;